"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export default function AddProductPage ({ isEdit = false, editingItem = null, onSectionChange }) {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [productSpecifications, setProductSpecifications] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSpecIndex, setEditingSpecIndex] = useState(null);
    const [modalFormData, setModalFormData] = useState({});
    const [deleteModal, setDeleteModal] = useState({
        show: false,
        type: 'table', // 'table' or 'spec'
        tableIndex: null,
        specId: null
    });
    const [viewSpecsModal, setViewSpecsModal] = useState(false);
    const navigate = useNavigate();

    const specificationFields = {
        size: {
            title: "Size Specifications",
            fields: [
                { name: "size_length1", label: "Length 1", type: "text", required: false },
                { name: "size_width1", label: "Width 1", type: "text", required: false },
                { name: "size_length2", label: "Length 2", type: "text", required: false },
                { name: "size_width2", label: "Width 2", type: "text", required: false },
            ],
        },
        thickness: {
            title: "Thickness Options",
            fields: [
                { name: "thickness_option1", label: "Thickness Option 1", type: "text", required: false },
                { name: "thickness_option2", label: "Thickness Option 2", type: "text", required: false },
            ],
        },
        underpad: {
            title: "Underpad Configuration",
            fields: [
                { name: "underpad_thickness", label: "Underpad Thickness", type: "text", required: false },
            ],
        },
        wearResistant: {
            title: "Wear Resistant Layer",
            fields: [
                { name: "wear_layer1", label: "Layer 1 (mil)", type: "text", required: false },
                { name: "wear_layer2", label: "Layer 2 (mil)", type: "text", required: false },
            ],
        },
        price: {
            title: "Price",
            fields: [
                { name: "Price", label: "Price", type: "number", required: false },
            ],
        },
        inventory: {
            title: "Inventory",
            fields: [
                { name: "Inventory", label: "Inventory", type: "number", required: false },
            ],
        },
    };

    const [formData, setFormData] = useState({
        product_name: "",
        description: "",
        short_description: "",
        price: "",
        inventory: "",
        category_id: "",
        images: [],
    });

    // Fetch categories on component mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch("https://leapatinternational.com/admin/api/v1/category.php");
                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
                const result = await response.json();
                setCategories(result.data || []);
            } catch (error) {
                console.error("Error fetching categories:", error);
                alert("Failed to load categories");
                setCategories([]);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    // Cleanup object URLs to prevent memory leaks
    useEffect(() => {
        return () => {
            imagePreviews.forEach(preview => {
                if (preview.url && preview.url.startsWith('blob:')) {
                    URL.revokeObjectURL(preview.url);
                }
            });
        };
    }, [imagePreviews]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const newImages = [];
        const newPreviews = [];

        files.forEach((file, index) => {
            const isDuplicate = formData.images.some(existingImg =>
                existingImg.name === file.name && existingImg.file.size === file.size
            );

            if (isDuplicate) {
                alert(`File "${file.name}" is already selected.`);
                return;
            }

            if (!file.type.startsWith('image/')) {
                alert(`File "${file.name}" is not an image.`);
                return;
            }

            const imageId = Date.now() + index;
            newImages.push({ id: imageId, file, name: file.name });
            newPreviews.push({ id: imageId, url: URL.createObjectURL(file), name: file.name });
        });

        if (newImages.length > 0) {
            setFormData(prev => ({ ...prev, images: [...prev.images, ...newImages] }));
            setImagePreviews(prev => [...prev, ...newPreviews]);
            alert(`${newImages.length} image(s) added successfully!`);
        }

        e.target.value = '';
    };

    const handleRemoveImage = (imageId) => {
        const previewToRemove = imagePreviews.find(preview => preview.id === imageId);
        if (previewToRemove?.url?.startsWith('blob:')) {
            URL.revokeObjectURL(previewToRemove.url);
        }

        setFormData(prev => ({ ...prev, images: prev.images.filter(img => img.id !== imageId) }));
        setImagePreviews(prev => prev.filter(preview => preview.id !== imageId));
    };

    const handleRemoveAllImages = () => {
        imagePreviews.forEach(preview => {
            if (preview.url?.startsWith('blob:')) {
                URL.revokeObjectURL(preview.url);
            }
        });

        setFormData(prev => ({ ...prev, images: [] }));
        setImagePreviews([]);
    };

    // Modal Functions
    const openModal = (specIndex = null) => {
        setEditingSpecIndex(specIndex);

        if (specIndex !== null) {
            const existingSpecs = productSpecifications[specIndex];
            const initialData = {};

            Object.keys(specificationFields).forEach(specType => {
                specificationFields[specType].fields.forEach(field => {
                    initialData[field.name] = "";
                });
            });

            existingSpecs.forEach(spec => {
                if (spec.type === "size") {
                    initialData.size_length1 = spec.data.length1 || "";
                    initialData.size_width1 = spec.data.width1 || "";
                    initialData.size_length2 = spec.data.length2 || "";
                    initialData.size_width2 = spec.data.width2 || "";
                } else if (spec.type === "thickness") {
                    initialData.thickness_option1 = spec.data.option1 || "";
                    initialData.thickness_option2 = spec.data.option2 || "";
                } else if (spec.type === "underpad") {
                    initialData.underpad_thickness = spec.data.thickness || "";
                } else if (spec.type === "wear-resistant") {
                    initialData.wear_layer1 = spec.data.layer1 || "";
                    initialData.wear_layer2 = spec.data.layer2 || "";
                } else if (spec.type === "price") {
                    initialData.Price = spec.data.Price || "";
                } else if (spec.type === "inventory") {
                    initialData.Inventory = spec.data.Inventory || "";
                }
            });

            setModalFormData(initialData);
        } else {
            const initialData = {};
            Object.keys(specificationFields).forEach(specType => {
                specificationFields[specType].fields.forEach(field => {
                    initialData[field.name] = "";
                });
            });
            setModalFormData(initialData);
        }

        setIsModalOpen(true);
    };

    const handleModalInputChange = (e) => {
        const { name, value } = e.target;
        setModalFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleModalSubmit = () => {
        const addedSpecs = [];

        // Check Size specifications
        const sizeData = {
            length1: modalFormData.size_length1,
            width1: modalFormData.size_width1,
            length2: modalFormData.size_length2,
            width2: modalFormData.size_width2,
        };
        if (Object.values(sizeData).some(val => val?.trim())) {
            addedSpecs.push({
                id: Date.now() + Math.random(),
                type: "size",
                data: sizeData
            });
        }

        // Check Thickness specifications
        const thicknessData = {
            option1: modalFormData.thickness_option1,
            option2: modalFormData.thickness_option2,
        };
        if (Object.values(thicknessData).some(val => val?.trim())) {
            addedSpecs.push({
                id: Date.now() + Math.random() + 1,
                type: "thickness",
                data: thicknessData
            });
        }

        // Check Underpad specifications
        const underpadData = {
            thickness: modalFormData.underpad_thickness,
        };
        if (Object.values(underpadData).some(val => val?.trim())) {
            addedSpecs.push({
                id: Date.now() + Math.random() + 2,
                type: "underpad",
                data: underpadData
            });
        }

        // Check Wear Resistant specifications
        const wearData = {
            layer1: modalFormData.wear_layer1,
            layer2: modalFormData.wear_layer2,
        };
        if (Object.values(wearData).some(val => val?.trim())) {
            addedSpecs.push({
                id: Date.now() + Math.random() + 3,
                type: "wear-resistant",
                data: wearData
            });
        }

        // Check Price
        const priceData = {
            Price: modalFormData.Price,
        };
        if (Object.values(priceData).some(val => val?.toString().trim())) {
            addedSpecs.push({
                id: Date.now() + Math.random() + 4,
                type: "price",
                data: priceData
            });
        }

        // Check Inventory
        const inventoryData = {
            Inventory: modalFormData.Inventory,
        };
        if (Object.values(inventoryData).some(val => val?.toString().trim())) {
            addedSpecs.push({
                id: Date.now() + Math.random() + 5,
                type: "inventory",
                data: inventoryData
            });
        }

        if (addedSpecs.length === 0) {
            alert("Please fill at least one specification field.");
            return;
        }

        if (editingSpecIndex !== null) {
            setProductSpecifications(prev => {
                const updated = [...prev];
                updated[editingSpecIndex] = addedSpecs;
                return updated;
            });
            alert("Specification table updated successfully!");
        } else {
            setProductSpecifications(prev => [...prev, addedSpecs]);
            alert("New specification table added successfully!");
        }

        setIsModalOpen(false);
        setEditingSpecIndex(null);
        setModalFormData({});
    };

    // Updated Delete Functions - Support both table and individual spec deletion
    const openDeleteModal = (type, tableIndex, specId = null) => {
        setDeleteModal({
            show: true,
            type,
            tableIndex,
            specId
        });
    };

    const confirmDelete = () => {
        if (deleteModal.type === 'table') {
            // Delete entire table
            setProductSpecifications(prev => prev.filter((_, index) => index !== deleteModal.tableIndex));
            alert("Specification table deleted successfully!");
        } else if (deleteModal.type === 'spec') {
            // Delete individual specification
            setProductSpecifications(prev => {
                const updated = [...prev];
                updated[deleteModal.tableIndex] = updated[deleteModal.tableIndex].filter(spec => spec.id !== deleteModal.specId);

                // If table becomes empty after deleting spec, remove the entire table
                if (updated[deleteModal.tableIndex].length === 0) {
                    updated.splice(deleteModal.tableIndex, 1);
                    alert("Specification deleted successfully! Table was removed as it became empty.");
                } else {
                    alert("Specification deleted successfully!");
                }

                return updated;
            });
        }

        setDeleteModal({ show: false, type: 'table', tableIndex: null, specId: null });
    };

    const cancelDelete = () => {
        setDeleteModal({ show: false, type: 'table', tableIndex: null, specId: null });
    };

    const getSpecTitle = (type) => {
        switch (type) {
            case "size":
                return "Size Specifications";
            case "thickness":
                return "Thickness Options";
            case "underpad":
                return "Underpad Configuration";
            case "wear-resistant":
                return "Wear Resistant Layer";
            case "price":
                return "Price";
            case "inventory":
                return "Inventory";
            default:
                return type;
        }
    };

    const getSpecValues = (spec) => {
        switch (spec.type) {
            case "size":
                return Object.values(spec.data).filter(val => val?.trim());
            case "thickness":
                return Object.values(spec.data).filter(val => val?.trim());
            case "underpad":
                return Object.values(spec.data).filter(val => val?.trim());
            case "wear-resistant":
                return Object.values(spec.data).filter(val => val?.trim());
            case "price":
                return Object.values(spec.data).filter(val => val?.toString().trim());
            case "inventory":
                return Object.values(spec.data).filter(val => val?.toString().trim());
            default:
                return [];
        }
    };

    //     const handleSubmit = async (e) => {
    //         e.preventDefault();
    //         setIsSubmitting(true);

    //         try {
    //             const data = new FormData();
    //             data.append("product_name", formData.product_name);
    //             data.append("description", formData.description);
    //             data.append("short_description", formData.short_description);
    //             data.append("price", formData.price);
    //             data.append("inventory", formData.inventory);
    //             data.append("category_id", formData.category_id);

    //             const formattedSpecifications = productSpecifications.map((specSet, index) => {
    //                 const productSpecs = {};
    //                 specSet.forEach(spec => {
    //                     productSpecs[spec.type] = spec.data;
    //                 });
    //                 return { [`product${index + 1}`]: productSpecs };
    //             });

    //             data.append("specifications", JSON.stringify(formattedSpecifications));

    //             formData.images.forEach(imageObj => {
    //                 data.append("image[]", imageObj.file);
    //             });
    // console.log(data,"this is data")
    //             const response = await fetch("https://leapatinternational.com/admin/api/v1/product_add.php", {
    //                 method: "POST",
    //                 body: data,
    //             });
    // console.log(response,"this is response")
    //             const result = await response.json();

    //             if (result.ok === true || result.status === "success") {
    //                 alert("Product created successfully!");
    //                 navigate('/dashboard/products');
    //             } else {
    //                 alert(result.message || "Failed to create product");
    //             }
    //         } catch (err) {
    //             console.error("Error:", err);
    //             alert("Network error. Please try again.");
    //         } finally {
    //             setIsSubmitting(false);
    //         }
    //     };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const data = new FormData();
            data.append("product_name", formData.product_name);
            data.append("description", formData.description);
            data.append("short_description", formData.short_description);
            data.append("price", formData.price);
            data.append("inventory", formData.inventory);
            data.append("category_id", formData.category_id);

            const formattedSpecifications = productSpecifications.map((specSet, index) => {
                const productSpecs = {};
                specSet.forEach(spec => {
                    productSpecs[spec.type] = spec.data;
                });
                return { [`product${index + 1}`]: productSpecs };
            });

            data.append("specifications", JSON.stringify(formattedSpecifications));

            // Enhanced image handling with proper naming
            formData.images.forEach((imageObj, index) => {
                // Ensure the file has proper extension
                let fileName = imageObj.name;
                if (!fileName.includes('.')) {
                    // Add extension based on file type
                    const extension = imageObj.type.split('/')[1] || 'jpg';
                    fileName = `${fileName}.${extension}`;
                }

                // Create a new File object with proper name if needed
                const fileToUpload = new File([imageObj.file], fileName, {
                    type: imageObj.type || imageObj.file.type
                });

                data.append("image[]", fileToUpload);
            });

            // Log form data for debugging
            for (let [key, value] of data.entries()) {
                console.log(key, value);
            }

            const response = await fetch("https://leapatinternational.com/admin/api/v1/product_add.php", {
                method: "POST",
                body: data,
            });

            const result = await response.json();

            if (result.ok === true || result.status === "success") {
                alert("Product created successfully!");
                navigate('/dashboard/products');
            } else {
                console.error("API Error:", result);
                alert(result.message || "Failed to create product");
            }
        } catch (err) {
            console.error("Network Error:", err);
            alert("Network error. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (onSectionChange) {
            onSectionChange("products");
        } else {
            navigate('/dashboard/products');
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <div>Loading...</div>
            </div>
        );
    }

    return (
        <div style={{ margin: '0 auto', padding: '20px' }}>
            {/* Page Header */}
            <div style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h1 style={{ margin: '0 0 5px 0' }}>{isEdit ? "Edit Product" : "Add New Product"}</h1>
                        <p style={{ margin: '0', color: '#666' }}>
                            {isEdit ? "Update product information" : "Create a new product listing"}
                        </p>
                    </div>
                    <button
                        onClick={handleCancel}
                        style={{
                            padding: '8px 16px',
                            background: '#f0f0f0',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        ← Back to Products
                    </button>
                </div>
            </div>

            {/* Main Product Form */}
            <form onSubmit={handleSubmit} style={{ background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                    {/* Left Column */}
                    <div style={{ flex: '1', minWidth: '300px' }}>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Product Name *</label>
                            <input
                                type="text"
                                name="product_name"
                                value={formData.product_name}
                                onChange={handleInputChange}
                                placeholder="Enter product name"
                                required
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                            <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>Choose a clear, descriptive name for your product</small>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Category *</label>
                            <select
                                name="category_id"
                                value={formData.category_id}
                                onChange={handleInputChange}
                                required
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            >
                                <option value="">Select a category</option>
                                {categories.map((category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.title}
                                    </option>
                                ))}
                            </select>
                            <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>Choose the most appropriate category for this product</small>
                        </div>

                        {/* <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                            <div style={{ flex: '1' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Price *</label>
                                <input
                                    type="number"
                                    name="price"
                                    value={formData.price}
                                    onChange={handleInputChange}
                                    placeholder="0.00"
                                    min="0"
                                    step="0.01"
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px'
                                    }}
                                />
                                <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>Enter product price</small>
                            </div>
                            <div style={{ flex: '1' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Inventory *</label>
                                <input
                                    type="number"
                                    name="inventory"
                                    value={formData.inventory}
                                    onChange={handleInputChange}
                                    placeholder="0"
                                    min="0"
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px'
                                    }}
                                />
                                <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>Available stock quantity</small>
                            </div>
                        </div> */}
                    </div>

                    {/* Right Column - Image Upload */}
                    <div style={{ flex: '1', minWidth: '300px' }}>
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <label style={{ fontWeight: 'bold' }}>Product Images Gallery</label>
                                {imagePreviews.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={handleRemoveAllImages}
                                        style={{
                                            padding: '5px 10px',
                                            background: '#ffebee',
                                            border: '1px solid #ef9a9a',
                                            borderRadius: '4px',
                                            color: '#c62828',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Remove All
                                    </button>
                                )}
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                multiple
                                style={{ width: '100%', marginBottom: '10px' }}
                            />
                            <small style={{ color: '#666', display: 'block' }}>
                                Upload multiple product images (JPG, PNG, GIF).
                                <br />
                                <span style={{ color: '#666' }}>{formData.images.length}/10 images selected</span>
                                <br />
                                <span style={{ color: '#1976d2' }}>💡 You can select images multiple times to add more to your gallery</span>
                            </small>

                            {/* Image Gallery Preview */}
                            {imagePreviews.length > 0 && (
                                <div style={{ marginTop: '15px' }}>
                                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Image Gallery Preview</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px' }}>
                                        {imagePreviews.map((preview) => (
                                            <div key={preview.id} style={{ position: 'relative' }}>
                                                <img
                                                    src={preview.url}
                                                    alt={preview.name}
                                                    style={{
                                                        width: '100%',
                                                        height: '120px',
                                                        objectFit: 'cover',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ddd',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => window.open(preview.url, '_blank')}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveImage(preview.id)}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '5px',
                                                        right: '5px',
                                                        background: '#ff5252',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '50%',
                                                        width: '24px',
                                                        height: '24px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                    title="Remove image"
                                                >
                                                    ×
                                                </button>
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: '0',
                                                    left: '0',
                                                    right: '0',
                                                    background: 'rgba(0,0,0,0.7)',
                                                    color: 'white',
                                                    padding: '5px',
                                                    fontSize: '10px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {preview.name}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Full Width Fields */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Short Description</label>
                    <textarea
                        name="short_description"
                        value={formData.short_description}
                        onChange={handleInputChange}
                        rows="2"
                        placeholder="Enter a brief product summary"
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                        }}
                    ></textarea>
                    <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>Brief summary of the product (optional)</small>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Product Description</label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows="5"
                        placeholder="Enter detailed product description"
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                        }}
                    ></textarea>
                    <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                        Provide detailed information about the product features and benefits
                    </small>
                </div>

                {/* Product Specifications Section - Updated with Individual Delete Buttons */}
                <div style={{
                    background: '#fff',
                    padding: '20px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    marginBottom: '20px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <div>
                            <h2 style={{ margin: '0 0 5px 0' }}>Product Specifications</h2>

                        </div>
                        <button
                            type="button"
                            onClick={() => openModal()}
                            className="add-button"
                        >
                            + Add New Specification
                        </button>
                    </div>

                    {/* Display Multiple Specification Tables with Individual Delete Buttons */}
                    {productSpecifications.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                            {productSpecifications.map((specSet, tableIndex) => (
                                <div key={tableIndex} style={{
                                    border: '2px solid #e3f2fd',
                                    borderRadius: '8px',
                                    overflow: 'hidden'
                                }}>
                                    {/* Table Header */}
                                    <div style={{
                                        color: 'white',
                                        padding: '7px 20px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <h3 style={{ margin: '0', fontSize: '18px', color: "#fff" }}>
                                        </h3>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <button
                                                type="button"
                                                onClick={() => openModal(tableIndex)}
                                                style={{
                                                    padding: '8px 12px',
                                                    background: '#494EA1',
                                                    border: '1px solid rgba(255,255,255,0.3)',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    color: 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '5px'
                                                }}
                                                title="Edit this specification table"
                                            >
                                                ✏️ Edit
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => openDeleteModal('table', tableIndex)}
                                                style={{
                                                    padding: '8px 12px',
                                                    background: 'rgba(244,67,54,0.8)',
                                                    border: '1px solid rgba(244,67,54,0.3)',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    color: 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '5px'
                                                }}
                                                title="Delete this specification table"
                                            >
                                                🗑️ Delete
                                            </button>
                                        </div>
                                    </div>

                                    {/* Table Content with Individual Delete Buttons */}
                                    <div style={{ background: 'white' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '2px solid #ddd', background: '#f8f9fa' }}>
                                                    <th style={{
                                                        padding: '12px 15px',
                                                        textAlign: 'left',
                                                        fontWeight: 'bold',
                                                        color: '#333'
                                                    }}>
                                                        Specification Type
                                                    </th>
                                                    <th style={{
                                                        padding: '12px 15px',
                                                        textAlign: 'left',
                                                        fontWeight: 'bold',
                                                        color: '#333'
                                                    }}>
                                                        Values
                                                    </th>

                                                </tr>
                                            </thead>
                                            <tbody>
                                                {specSet.map((spec, specIndex) => (
                                                    <tr key={spec.id} style={{
                                                        borderBottom: '1px solid #eee',
                                                        backgroundColor: specIndex % 2 === 0 ? '#ffffff' : '#f9f9f9'
                                                    }}>
                                                        <td style={{
                                                            padding: '12px 15px',
                                                            verticalAlign: 'top'
                                                        }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <span style={{ fontWeight: '500', color: '#333' }}>
                                                                    {getSpecTitle(spec.type)}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td style={{
                                                            padding: '12px 15px',
                                                            verticalAlign: 'top', width: '45%'
                                                        }}>
                                                            <div style={{
                                                                display: 'flex',
                                                                flexWrap: 'wrap',
                                                                gap: '6px',
                                                                alignItems: 'center'
                                                            }}>
                                                                {getSpecValues(spec).map((value, valueIndex) => (
                                                                    <span key={valueIndex} style={{
                                                                        background: '#e3f2fd',
                                                                        color: '#1976d2',
                                                                        padding: '4px 8px',
                                                                        borderRadius: '4px',
                                                                        fontSize: '13px',
                                                                        border: '1px solid #bbdefb',
                                                                        fontWeight: '500'
                                                                    }}>
                                                                        {value}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </td>

                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666', background: '#f8f9fa', borderRadius: '6px', marginTop: '15px' }}>
                            <div style={{ fontSize: '48px', marginBottom: '15px' }}>📋</div>
                            <p style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: '500' }}>No specification added yet</p>
                            <p style={{ margin: '0', fontSize: '14px' }}>Click the "Add New Specification" button above to get started.</p>
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', gap: '10px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                    <button
                        type="submit"
                        className="add-button"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span style={{ marginRight: '5px' }}>Loading...</span>
                            </>
                        ) : (
                            <>{isEdit ? "Update Product" : "Create Product"}</>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                        className="back-button pe-3 ps-3"
                    >
                        Cancel
                    </button>
                </div>
            </form>

            {/* Specifications Modal */}
            {isModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: '0',
                    left: '0',
                    right: '0',
                    bottom: '0',
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: '1000'
                }}>
                    <div style={{
                        background: 'white',
                        padding: '25px',
                        borderRadius: '8px',
                        width: '100%',
                        maxWidth: '800px',
                        maxHeight: '85vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                            <h2 style={{ margin: '0', color: '#333' }}>
                                {editingSpecIndex !== null ? `Edit Specification Product ${editingSpecIndex + 1}` : "Add New Specification Product"}
                            </h2>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#666'
                                }}
                            >
                                ×
                            </button>
                        </div>


                        {/* All Specifications in One Screen */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                            {/* Size Specifications */}
                            <div style={{
                                background: '#f8f9fa',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid #e9ecef'
                            }}>
                                <h3 style={{ margin: '0 0 15px 0', color: '#2196F3', fontSize: '18px' }}>
                                    Size Specifications
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: '15px'
                                }}>
                                    {specificationFields.size.fields.map(field => (
                                        <div key={field.name}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                                {field.label}
                                            </label>
                                            <input
                                                type={field.type}
                                                name={field.name}
                                                value={modalFormData[field.name] || ""}
                                                onChange={handleModalInputChange}
                                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px'
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Thickness Specifications */}
                            <div style={{
                                background: '#f8f9fa',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid #e9ecef'
                            }}>
                                <h3 style={{ margin: '0 0 15px 0', color: '#ff9800', fontSize: '18px' }}>
                                    Thickness Options
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: '15px'
                                }}>
                                    {specificationFields.thickness.fields.map(field => (
                                        <div key={field.name}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                                {field.label}
                                            </label>
                                            <input
                                                type={field.type}
                                                name={field.name}
                                                value={modalFormData[field.name] || ""}
                                                onChange={handleModalInputChange}
                                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px'
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Underpad Specifications */}
                            <div style={{
                                background: '#f8f9fa',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid #e9ecef'
                            }}>
                                <h3 style={{ margin: '0 0 15px 0', color: '#4caf50', fontSize: '18px' }}>
                                    Underpad Configuration
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr',
                                    gap: '15px'
                                }}>
                                    {specificationFields.underpad.fields.map(field => (
                                        <div key={field.name}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                                {field.label}
                                            </label>
                                            <input
                                                type={field.type}
                                                name={field.name}
                                                value={modalFormData[field.name] || ""}
                                                onChange={handleModalInputChange}
                                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px'
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Wear Resistant Specifications */}
                            <div style={{
                                background: '#f8f9fa',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid #e9ecef'
                            }}>
                                <h3 style={{ margin: '0 0 15px 0', color: '#9c27b0', fontSize: '18px' }}>
                                    Wear Resistant Layer
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: '15px'
                                }}>
                                    {specificationFields.wearResistant.fields.map(field => (
                                        <div key={field.name}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                                {field.label}
                                            </label>
                                            <input
                                                type={field.type}
                                                name={field.name}
                                                value={modalFormData[field.name] || ""}
                                                onChange={handleModalInputChange}
                                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px'
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div style={{
                                background: '#f8f9fa',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid #e9ecef'
                            }}>
                                <h3 style={{ margin: '0 0 15px 0', color: '#9c27b0', fontSize: '18px' }}>
                                    Price *
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: '15px'
                                }}>
                                    {specificationFields.price.fields.map(field => (
                                        <div key={field.name}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                                {field.label}
                                            </label>
                                            <input
                                                type={field.type}
                                                name={field.name}
                                                value={modalFormData[field.name] || ""}
                                                onChange={handleModalInputChange}
                                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px'
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div style={{
                                background: '#f8f9fa',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid #e9ecef'
                            }}>
                                <h3 style={{ margin: '0 0 15px 0', color: '#9c27b0', fontSize: '18px' }}>
                                    Inventory *
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: '15px'
                                }}>
                                    {specificationFields.inventory.fields.map(field => (
                                        <div key={field.name}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                                {field.label}
                                            </label>
                                            <input
                                                type={field.type}
                                                name={field.name}
                                                value={modalFormData[field.name] || ""}
                                                onChange={handleModalInputChange}
                                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px'
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '25px' }}>
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="back-button pe-3 ps-3"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleModalSubmit}
                                className="add-button"
                            >
                                {editingSpecIndex !== null ? "Update Product" : "Add New Product"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Enhanced Delete Confirmation Modal - Supports both table and spec deletion */}
            {deleteModal.show && (
                <div style={{
                    position: 'fixed',
                    top: '0',
                    left: '0',
                    right: '0',
                    bottom: '0',
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: '1001'
                }}>
                    <div style={{
                        background: 'white',
                        padding: '25px',
                        borderRadius: '8px',
                        width: '100%',
                        maxWidth: '400px',
                        textAlign: 'center'
                    }}>
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{
                                fontSize: '48px',
                                color: '#ff9800',
                                marginBottom: '15px'
                            }}>
                                ⚠️
                            </div>
                            <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>
                                {deleteModal.type === 'table' ? 'Delete Specification Table' : 'Delete Specification'}
                            </h3>
                            <p style={{ margin: '0', color: '#666', lineHeight: '1.5' }}>
                                {deleteModal.type === 'table' ? (
                                    <>
                                        Are you sure you want to delete <strong>Specification Product {deleteModal.tableIndex + 1}</strong>?
                                        <br />
                                        <span style={{ color: '#d32f2f', fontSize: '14px' }}>
                                            This will delete all specifications in this table.
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        Are you sure you want to delete this specification from <strong>Product {deleteModal.tableIndex + 1}</strong>?
                                        <br />
                                        <span style={{ color: '#d32f2f', fontSize: '14px' }}>
                                            This action cannot be undone.
                                        </span>
                                    </>
                                )}
                            </p>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={cancelDelete}
                                style={{
                                    padding: '10px 20px',
                                    background: '#f5f5f5',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmDelete}
                                style={{
                                    padding: '10px 20px',
                                    background: '#d32f2f',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                {deleteModal.type === 'table' ? 'Delete Table' : 'Delete Specification'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

